import React, { FC, useEffect, useState } from "react";
import { getAcceptableLocationTypes, KRT, LocationFieldsProps } from "../LocationFields";
import EditableField, { EditableFieldTypes } from "../../../../components/editable/EditableField/EditableField";
import { GridLegacy as Grid } from "@mui/material";
import { MovementRuleAction, api, AssetLocation, AssetLocationTypes, ITimeSlot, KeyStorageRule } from "../../../../logic/api";
import { useTranslation } from "react-i18next";
import { IDropDownListItem } from "../../../../components/editable/EditableField/fields/DropDownField";
import { formatCamel } from "../../../../utility/utils";
import { ReleaseFlag, useReleaseManagement } from "../../../../utility/feature-management";

type SRT = keyof typeof KeyStorageRule;

export const KeyLocationFields: FC<LocationFieldsProps> = ({ location, parentLocation, session }) => {
  const isZoneCheckReleased = useReleaseManagement(ReleaseFlag.releaseZoneCheck);
  const isRbacLocationsManagementReleased = useReleaseManagement(ReleaseFlag.releaseRbacLocationsManagement);

  const acceptableLocationTypes = getAcceptableLocationTypes(true, parentLocation);
  const getTimeslots = api.hooks.kmosV2.getTimeSlots();
  const getZones = api.hooks.assetLocation.getByParent();

  const [timeslotList, setTimeslotList] = useState<ITimeSlot[]>([]);
  const [zoneList, setZoneList] = useState<IDropDownListItem[]>([]);

  useEffect(() => {
    getTimeslots.send(session.current.context, {}, (responseData: ITimeSlot[] | null) => {
      if (responseData) {
        setTimeslotList(responseData);
      }
    })
  }, []);

  useEffect(() => {
    if (session.current.site?.home?.id) {
      getZones.send(session.current.context, {
        parentId: session.current.site.home.id,
        types: [AssetLocationTypes.Zone],
        includeInActive: false
      }, (zones) => {
        if (zones) {
          const items = zones.map(zone => ({
            name: zone.name,
            value: zone.id.toString()
          }));
          setZoneList(items);
        }
      });
    }
  }, [session.current.site?.home?.id]);

  const { t } = useTranslation();

  const onChangeTimeslot = (data: AssetLocation, item: IDropDownListItem) => {
    // had to do this to appease intellisense
    const value = item.value;

    if (value && session.current.site?.home) {
      const timeslot: Partial<ITimeSlot> | undefined = timeslotList.find(timeslot => timeslot.id === parseInt(value));
      if (timeslot) {
        data.keyStorageTimeslotId = timeslot.id || null;
      }
    }
  }

  const onProvideTimeslot = (data: AssetLocation) => {
    const timeslot = timeslotList.find(timeslot => timeslot.id === data.keyStorageTimeslotId);

    return {
      name: timeslot?.segment.toString() || "",
      value: timeslot?.id.toString() || ""
    }
  }

  const getKeyStorageRuleLabel = (ruleName: string) => {
    if (ruleName.toUpperCase() === "ANY") {
      return t("input.labels.any")
    }

    if (ruleName.toUpperCase() === "DATE") {
      return t("input.labels.date")
    }

    if (ruleName.toUpperCase() === "DATETIMESLOT") {
      return t("input.labels.dateTimeSlot")
    }

    return "";
  }

  return (<Grid container>
    <EditableField<AssetLocation, string>
      testId="key-location-name-field"
      inputType={EditableFieldTypes.text}
      label={t("input.labels.name")}
      onProvide={(data) => data.name}
      onChange={(data, value) => data.name = value}
    />
    <EditableField<AssetLocation, IDropDownListItem>
      testId="key-location-type-field"
      inputType={EditableFieldTypes.terminalField}
      alwaysVisible={!isRbacLocationsManagementReleased}
      readOnly={Boolean(location.id)}
      label={t("locations.type")}
      onProvide={(data) => {
        const typeName = data.type != null
          ? formatCamel(AssetLocationTypes[data.type]?.toString() ?? "")
          : "";
        return {
          name: typeName,
          value: data.type == null ? "" : data.type.toString()
        };
      }}
      onChange={(data, item) => {
        data.type = parseInt((item.value ?? "").toString());
      }}
      onRequestData={async () => {
        const items: IDropDownListItem[] = [];
        for (const pt in AssetLocationTypes) {
          const intVal = parseInt(pt);
          if (isNaN(intVal)) {
            if (acceptableLocationTypes.indexOf(parseInt(AssetLocationTypes[pt])) != -1) {
              items.push({ name: formatCamel(pt.toString()), value: AssetLocationTypes[pt] });
            }
          }
        }
        return items;
      }}
    />

    <div style={{ "width": "100%", marginTop: 40 }}>
      <b>{t("locations.attributes")}</b>
    </div>

    <EditableField<AssetLocation, boolean>
      testId="key-location-arrivals-switch"
      inputType={EditableFieldTypes.switch}
      label={t("input.labels.arrivals")}
      onProvide={(data) => data.arrivals}
      onChange={(data, value) => {
        data.arrivals = value;
        if (value === true) {
          data.blockPark = false;
        }
      }}
    />
    <EditableField<AssetLocation, boolean>
      testId="key-location-returns-switch"
      inputType={EditableFieldTypes.switch}
      label={t("input.labels.returns")}
      onProvide={(data) => data.returns}
      onChange={(data, value) => {
        data.returns = value;
        if (value === true) {
          data.blockPark = false;
        }
      }}
    />

    <EditableField<AssetLocation, number>
      testId="key-location-capacity-field"
      inputType={EditableFieldTypes.number}
      label={t("kmos.labels.capacity")}
      onProvide={(data) => data.capacity}
      onChange={(data, value) => data.capacity = value}
    />

    <div style={{ "width": "100%", marginTop: 40 }}>
      <b>{t("locations.rules")}</b>
    </div>

    <Grid container>
      {location.type === AssetLocationTypes.DynamicBox &&
        <EditableField<AssetLocation, boolean>
          testId="key-location-first-key-switch"
          inputType={EditableFieldTypes.switch}
          label={t("input.labels.firstKey")}
          onProvide={(data) => data.noMixedLaneAllocations ?? true}
          onChange={(data, value) => {
            data.noMixedLaneAllocations = value;
          }}
        />
      }

      {location.type !== AssetLocationTypes.DynamicBox &&
        <>

          <EditableField<AssetLocation, boolean>
            testId="key-location-alert-overfilled-switch"
            inputType={EditableFieldTypes.switch}
            label={t("input.labels.alertIfOverfilled")}
            onProvide={(data) => data.alertIfOverfilled ?? false}
            onChange={(data, value) => {
              data.alertIfOverfilled = value;
            }}
          />


          <EditableField<AssetLocation, IDropDownListItem>
            testId="key-location-store-keys-by-field"
            inputType={EditableFieldTypes.terminalField}
            alwaysVisible={!isRbacLocationsManagementReleased}
            label={t("input.labels.storeKeysBy")}
            onProvide={(data) => {
              return {
                name: data.keyStorageRule ? getKeyStorageRuleLabel(KeyStorageRule[data.keyStorageRule]) : "",
                value: data.keyStorageRule?.toString() || ""
              };
            }}
            onChange={(data, item) => {
              if (item.value) {
                data.keyStorageRule = parseInt(item.value) as KeyStorageRule
              }
            }}
            onRequestData={async () => (
              Object.values(KeyStorageRule)
                .filter(val => typeof val === 'string') as Array<SRT>)
              .map((key) => {
                return {
                  name: getKeyStorageRuleLabel(key),
                  value: KeyStorageRule[key].toString()
                }
              }
              )}
          />

          {[KeyStorageRule.Date, KeyStorageRule.DateTimeSlot].includes(location.keyStorageRule) && (
            <>
              <EditableField<AssetLocation, IDropDownListItem>
                testId="key-location-mismatch-field"
                inputType={EditableFieldTypes.terminalField}
                alwaysVisible={!isRbacLocationsManagementReleased}
                label={t("input.labels.keyLocationMismatch")}
                onProvide={(data) => {
                  return {
                    name: MovementRuleAction[data.keyDEALRuleAction],
                    value: data.keyDEALRuleAction?.toString() || ""
                  };
                }}
                onChange={(data, item) => {
                  if (item.value) {
                    data.keyDEALRuleAction = parseInt(item.value) as MovementRuleAction
                  }
                }}
                onRequestData={async () => (

                  Object.values(MovementRuleAction)
                    .filter(val => typeof val === 'string') as Array<KRT>)
                  .filter(val => val !== MovementRuleAction[0].toString())
                  .map((key) => {
                    return {
                      name: key,
                      value: MovementRuleAction[key].toString()
                    }
                  })
                }
              />
              <EditableField<AssetLocation, IDropDownListItem>
                testId="key-location-day-of-month-field"
                inputType={EditableFieldTypes.terminalField}
                alwaysVisible={!isRbacLocationsManagementReleased}
                required
                label={t("input.labels.dayOfMonth")}
                onProvide={(data) => {
                  return {
                    name: data.keyStorageDate?.toString() || "",
                    value: data.keyStorageDate?.toString() || ""
                  };
                }}
                onChange={(data, item) => {
                  if (item.value) {
                    data.keyStorageDate = parseInt(item.value);
                  }
                }}
                onRequestData={async () => {
                  const dateOptions = [...Array(32).keys()].slice(1)
                    .map((key) => {
                      return {
                        name: key.toString(),
                        value: key.toString()
                      };
                    });

                  dateOptions.unshift({ name: "", value: "" });
                  return dateOptions;
                }}
              />
              {timeslotList.length > 0 && location.keyStorageRule === KeyStorageRule.DateTimeSlot && <EditableField<AssetLocation, IDropDownListItem>
                testId="key-location-timeslot-field"
                inputType={EditableFieldTypes.terminalField}
                alwaysVisible={!isRbacLocationsManagementReleased}
                required
                label={t("input.labels.timeslot")}
                onProvide={(data) => onProvideTimeslot(data)}
                onChange={(data, item) => onChangeTimeslot(data, item)}
                onRequestData={async () => {
                  const noTimeslot = { name: "", value: "" };
                  return [noTimeslot, ...timeslotList.map((key) => {
                    return {
                      name: key.segment.toString() || "",
                      value: key.id.toString() || ""
                    };
                  })];
                }}

              />}

              {isZoneCheckReleased &&
                <EditableField<AssetLocation, IDropDownListItem[]>
                  testId="key-location-only-accept-keys-field"
                  inputType={EditableFieldTypes.multiSelect}
                  alwaysVisible={!isRbacLocationsManagementReleased}
                  label={t("input.labels.onlyAcceptKeys")}
                  limitTags={2} // Show 2 chips, then "+X more"
                  onProvide={(data) => {
                    const ids = data.allowedParentIds || [];
                    if (!ids.length) return [];
                    return zoneList.filter(option =>
                      option.value != null && ids.includes(parseInt(option.value, 10))
                    );

                  }}
                  onChange={(data, items) => {
                    data.allowedParentIds = items?.length
                      ? items
                        .filter(item => item.value != null)
                        .map(item => parseInt(item.value!, 10))
                      : [];
                  }}
                  onRequestData={async () => {
                    return zoneList;
                  }}
                />
              }
            </>
          )}
        </>}
    </Grid>
  </Grid>)
}
